import * as THREE from 'three';
import { settings } from '@/config';
import { smoothingFactor } from '@/utils/math';
import type { TickState, Updatable, Vec3 } from '@/types';

const WORLD_UP = new THREE.Vector3(0, 1, 0);

function coerce(value: THREE.Vector3 | Vec3, out: THREE.Vector3): THREE.Vector3 {
  return value instanceof THREE.Vector3 ? out.copy(value) : out.set(value[0], value[1], value[2]);
}

/**
 * Completely independent camera motion layer. External drivers (scroll test,
 * future GSAP keyframes, baked GLB camera, orbit) only ever write the *target*
 * transform; this controller smoothly interpolates the render camera toward it
 * each frame (position lerp + quaternion slerp), exactly mirroring the proven
 * `renderCamera.follow(glbCamera)` pattern.
 *
 * A reusable `parallax` quaternion is post-multiplied onto the target rotation,
 * giving future mouse parallax a clean injection point without coupling.
 */
export class CameraController implements Updatable {
  readonly camera: THREE.PerspectiveCamera;
  readonly targetPosition = new THREE.Vector3();
  readonly targetQuaternion = new THREE.Quaternion();
  /** Future mouse parallax offset (identity = none). */
  readonly parallax = new THREE.Quaternion();
  targetFov: number;

  enabled = true;
  positionSmoothing = settings.camera.positionSmoothing;
  rotationSmoothing = settings.camera.rotationSmoothing;
  fovSmoothing = settings.camera.fovSmoothing;
  /** World units the render camera hangs BEHIND its target, along the target's own view axis —
   *  the story mode's respectful step back (2026-09-02, user: "when I click KNOW MORE the
   *  camera goes a little behind its current position"). Eased by the caller; applied here so
   *  the follow-lag clamp measures against the recoiled target and never fights it. */
  dolly = 0;

  private readonly _matrix = new THREE.Matrix4();
  private readonly _eye = new THREE.Vector3();
  private readonly _look = new THREE.Vector3();
  private readonly _finalQuat = new THREE.Quaternion();
  private readonly _effTarget = new THREE.Vector3();
  private readonly _dollyDir = new THREE.Vector3();

  constructor(camera: THREE.PerspectiveCamera) {
    this.camera = camera;
    this.targetPosition.copy(camera.position);
    this.targetQuaternion.copy(camera.quaternion);
    this.targetFov = camera.fov;
  }

  /** Aim the target transform from an eye position toward a look-at point. */
  lookFrom(position: THREE.Vector3 | Vec3, target: THREE.Vector3 | Vec3, up = WORLD_UP): void {
    coerce(position, this._eye);
    coerce(target, this._look);
    this._matrix.lookAt(this._eye, this._look, up);
    this.targetPosition.copy(this._eye);
    this.targetQuaternion.setFromRotationMatrix(this._matrix);
  }

  /** Set the target transform directly (used by the baked-camera driver). */
  setTarget(position: THREE.Vector3, quaternion: THREE.Quaternion, fov?: number): void {
    this.targetPosition.copy(position);
    this.targetQuaternion.copy(quaternion);
    if (fov !== undefined) this.targetFov = fov;
  }

  /**
   * Drive the FOV directly, bypassing the exponential ease (continuous zooms — the magnet
   * glide's lens pull). The caller's value is already perfectly smoothed per-frame, so routing
   * it through the smoothing here would only ADD lag — which is exactly what read as "the lens
   * keeps zooming after the camera stopped" (2026-09-01, user: "still not smooth").
   */
  setFovImmediate(fov: number): void {
    this.targetFov = fov;
    if (Math.abs(this.camera.fov - fov) > 1e-4) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  }

  /** Snap the render camera onto the current target with no smoothing. */
  commit(): void {
    this.camera.position.copy(this.targetPosition);
    this.camera.quaternion.copy(this.targetQuaternion);
    this.camera.fov = this.targetFov;
    this.camera.updateProjectionMatrix();
  }

  update(tick: TickState): void {
    if (!this.enabled) return;

    const aPos = smoothingFactor(this.positionSmoothing, tick.delta);
    const aRot = smoothingFactor(this.rotationSmoothing, tick.delta);

    // Effective target = the driven target, stepped back by the story recoil (see `dolly`).
    // Cameras look down -Z, so +Z of the target's orientation is straight backward.
    this._effTarget.copy(this.targetPosition);
    if (this.dolly !== 0) {
      this._dollyDir.set(0, 0, 1).applyQuaternion(this.targetQuaternion);
      this._effTarget.addScaledVector(this._dollyDir, this.dolly);
    }

    this.camera.position.lerp(this._effTarget, aPos);
    // FOLLOW-LAG CLAMP (2026-09-01, user: "when I scroll fast the camera passes through some
    // mesh"): bound how far the smoothed camera may trail its target. The lag direction is
    // kept (continuous — no pop), only its length is capped, so at speed the camera rides a
    // tight tube around the authored, collision-free path instead of corner-cutting through
    // walls; at a calm pace the lag never reaches the cap and the glide feel is unchanged.
    const maxLag = settings.camera.maxFollowLag;
    if (maxLag > 0) {
      const d = this.camera.position.distanceTo(this._effTarget);
      if (d > maxLag) {
        this.camera.position
          .sub(this._effTarget)
          .multiplyScalar(maxLag / d)
          .add(this._effTarget);
      }
    }
    this._finalQuat.copy(this.targetQuaternion).multiply(this.parallax);
    this.camera.quaternion.slerp(this._finalQuat, aRot);

    if (Math.abs(this.camera.fov - this.targetFov) > 1e-3) {
      // Gentler than the pose smoothing — a zoom's exponential onset is what reads as a
      // stutter (see settings.camera.fovSmoothing).
      const aFov = smoothingFactor(this.fovSmoothing, tick.delta);
      this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, this.targetFov, aFov);
      this.camera.updateProjectionMatrix();
    }
  }
}
